export function buildCsv(rows: any[], columns: Array<{ key: string; label: string }>): string {
  if (rows.length === 0) return columns.map(c => c.label).join(',');
  const header = columns.map(c => c.label).join(',');
  const body = rows.map(r =>
    columns.map(c => {
      const v = r[c.key];
      if (v === null || v === undefined) return '';
      const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')
  );
  return [header, ...body].join('\n');
}

/**
 * ─── The one CSV tokeniser. Do not hand-roll another. ────────────────────────────────────────────
 *
 * Every upload screen had its own copy of "split into lines, then split each line on commas", and
 * they all shared the same defect: **the text is cut into lines BEFORE quotes are understood.** A
 * field is allowed to contain the delimiter, and a quoted field is allowed to contain a NEWLINE —
 * both are ordinary in Indian addresses:
 *
 *     "B-225,Wing 4,S V Brindavanam Kadubeesanahalli, Panathur Road"
 *
 * With line-first splitting, a quoted newline tears one record into two, every later column shifts
 * left, and the whole sheet fails validation on rows that are perfectly good. That is what stopped
 * merchants uploading orders. NdrPage was worse still — a bare `line.split(',')` with no quote
 * handling at all, so any comma anywhere broke the row.
 *
 * This is a single pass over the WHOLE text, so a quoted field may contain the delimiter, quotes
 * (doubled, per RFC 4180) and line breaks.
 *
 * It also handles the three things real spreadsheets do that trip people up:
 *
 *   • **BOM** — Excel writes ﻿ at the start of a UTF-8 CSV. Left in place it becomes part of
 *     the FIRST header, so `orderNo` silently stops matching and every row reports a missing
 *     order number.
 *   • **Delimiter** — copying cells out of Excel gives TAB-separated text, and Excel in several
 *     locales writes SEMICOLON-separated ".csv". Both are auto-detected from the header line, so a
 *     paste works as well as a saved file.
 *   • **Whitespace** — unquoted values are trimmed. Quoted values are returned verbatim, because
 *     the quotes are the author saying "this is exactly what I mean".
 */
export type Delimiter = ',' | '\t' | ';' | '|';

/** Pick the delimiter by counting candidates OUTSIDE quotes on the header line. */
export function detectDelimiter(text: string): Delimiter {
  const candidates: Delimiter[] = [',', '\t', ';', '|'];
  // Only look at the first line — but find it without breaking a quoted newline.
  let headerEnd = -1;
  let inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '"') { inQ = !inQ; continue; }
    if (!inQ && (c === '\n' || c === '\r')) { headerEnd = i; break; }
  }
  const header = headerEnd === -1 ? text : text.slice(0, headerEnd);

  let best: Delimiter = ',';
  let bestCount = 0;
  for (const d of candidates) {
    let count = 0;
    let q = false;
    for (let i = 0; i < header.length; i++) {
      const c = header[i];
      if (c === '"') { q = !q; continue; }
      if (!q && c === d) count++;
    }
    if (count > bestCount) { best = d; bestCount = count; }
  }
  return best;
}

/**
 * Tokenise delimited text into rows of raw cells. One pass, quote-aware, newline-safe.
 * Returns every record including the header; blank records are dropped.
 */
export function parseDelimitedRows(text: string, delimiter?: Delimiter): string[][] {
  const src = String(text ?? '').replace(/^﻿/, '');
  if (!src.trim()) return [];
  const d = delimiter ?? detectDelimiter(src);

  const rows: string[][] = [];
  let row: string[] = [];
  let cur = '';
  let inQ = false;
  let quoted = false;          // was THIS cell quoted? decides whether to trim it

  const endCell = () => { row.push(quoted ? cur : cur.trim()); cur = ''; quoted = false; };
  const endRow = () => {
    endCell();
    // Drop records that are entirely empty — trailing newlines, and the blank lines spreadsheets
    // leave behind. A row with real values in some columns is kept.
    if (row.some((c) => c !== '')) rows.push(row);
    row = [];
  };

  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQ) {
      if (c === '"') {
        if (src[i + 1] === '"') { cur += '"'; i++; }   // escaped quote
        else inQ = false;
      } else cur += c;                                  // includes , ; \t and newlines
      continue;
    }
    if (c === '"') { inQ = true; quoted = true; continue; }
    if (c === d) { endCell(); continue; }
    if (c === '\r') { if (src[i + 1] === '\n') i++; endRow(); continue; }
    if (c === '\n') { endRow(); continue; }
    cur += c;
  }
  // Whatever is left after the last newline.
  if (cur !== '' || row.length) endRow();
  return rows;
}

/**
 * Header + objects. `mapHeader` lets a caller canonicalise column names (aliases, lower-casing)
 * without re-implementing the tokeniser.
 */
export function parseDelimited(
  text: string,
  opts: { mapHeader?: (h: string) => string } = {},
): { headers: string[]; rows: Record<string, string>[]; delimiter: Delimiter } {
  const delimiter = detectDelimiter(String(text ?? '').replace(/^﻿/, ''));
  const raw = parseDelimitedRows(text, delimiter);
  if (raw.length === 0) return { headers: [], rows: [], delimiter };

  const headers = raw[0].map((h) => (opts.mapHeader ? opts.mapHeader(h) : h));
  const rows = raw.slice(1).map((cells) => {
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => { obj[h] = cells[i] ?? ''; });
    return obj;
  });
  return { headers, rows, delimiter };
}

/** Back-compat wrapper — same shape the old hand-rolled `parseCsv` returned. */
export function parseCsv(content: string): { headers: string[]; rows: Record<string, string>[] } {
  const { headers, rows } = parseDelimited(content);
  return { headers, rows };
}
